import React, { createContext, useContext, useState, useEffect } from 'react';
import productImg1 from '../assets/WhatsApp Image 2026-04-07 at 10.27.07 AM.jpeg';
import productImg2 from '../assets/WhatsApp Image 2026-04-07 at 10.27.56 AM (2).jpeg';
import productImg3 from '../assets/WhatsApp Image 2026-04-07 at 10.27.56 AM.jpeg';
import productImg4 from '../assets/WhatsApp Image 2026-04-07 at 10.27.58 AM (1).jpeg';
import productImg5 from '../assets/WhatsApp Image 2026-04-07 at 10.27.58 AM (2).jpeg';
import productImg6 from '../assets/WhatsApp Image 2026-04-07 at 10.27.58 AM.jpeg';

const ProductContext = createContext();

const DEFAULT_PRODUCT_IMAGES = {
  1: productImg1,
  2: productImg2,
  3: productImg3,
  4: productImg4,
  5: productImg5,
  6: productImg6,
};

/** Replace seeded Google CDN images with bundled assets (existing localStorage). */
function migrateProductImages(products) {
  if (!Array.isArray(products)) return products;
  return products.map((p) => {
    const local = DEFAULT_PRODUCT_IMAGES[p.id];
    if (!local || !p.image || typeof p.image !== 'string') return p;
    if (p.image.startsWith('https://lh3.googleusercontent.com')) {
      return { ...p, image: local };
    }
    return p;
  });
}

export const useProducts = () => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error('useProducts must be used within a ProductProvider');
  }
  return context;
};

export const ProductProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [showToast, setShowToast] = useState({ show: false, message: '', type: '' });

  // Load products from localStorage on mount
  useEffect(() => {
    const savedProducts = localStorage.getItem('products');
    if (savedProducts) {
      const parsedProducts = JSON.parse(savedProducts);
      const migrated = migrateProductImages(parsedProducts);
      setProducts(migrated);
      if (JSON.stringify(migrated) !== JSON.stringify(parsedProducts)) {
        localStorage.setItem('products', JSON.stringify(migrated));
      }
    } else {
      // Default products
      const defaultProducts = [
        {
          id: 1,
          name: 'The Arches Chair',
          price: '₹2,450.00',
          description: 'Elegant lounge chair featuring curved arches and premium upholstery. Perfect blend of comfort and sophisticated design.',
          image: productImg1,
        },
        {
          id: 2,
          name: 'Linear Oak Buffet',
          price: '₹4,100.00',
          description: 'Minimalist sideboard with seamless grain pattern and brass hardware. Clean lines meet natural beauty.',
          image: productImg2,
        },
        {
          id: 3,
          name: 'Shadow Table',
          price: '₹1,890.00',
          description: 'Hand-carved coffee table with organic asymmetrical shape. Each piece tells a unique story of craftsmanship.',
          image: productImg3,
        },
        {
          id: 4,
          name: 'Vessel Nightstand',
          price: '₹950.00',
          description: 'Compact bedside table with elegant curves and functional storage. Modern design meets practical utility.',
          image: productImg4,
        },
        {
          id: 5,
          name: 'Wave Bookshelf',
          price: '₹3,200.00',
          description: 'Curved shelving unit with dynamic wave pattern. Perfect for displaying books and decorative items.',
          image: productImg5,
        },
        {
          id: 6,
          name: 'Zen Console',
          price: '₹2,750.00',
          description: 'Minimalist console table with clean lines and subtle details. Perfect for entryways and hallways.',
          image: productImg6,
        },
      ];
      setProducts(defaultProducts);
      localStorage.setItem('products', JSON.stringify(defaultProducts));
    }
  }, []);

  // Save products to localStorage whenever they change
  useEffect(() => {
    if (products.length > 0) {
      localStorage.setItem('products', JSON.stringify(products));
    }
  }, [products]);

  const showToastNotification = (message, type) => {
    setShowToast({ show: true, message, type });
    setTimeout(() => {
      setShowToast({ show: false, message: '', type: '' });
    }, 3000);
  };

  const addProduct = (newProduct) => {
    const productWithId = {
      ...newProduct,
      id: Date.now()
    };
    setProducts(prev => [...prev, productWithId]);
    showToastNotification('Product added successfully!', 'success');
  };

  const updateProduct = (updatedProduct) => {
    setProducts(prev => 
      prev.map(product => 
        product.id === updatedProduct.id ? updatedProduct : product
      )
    );
    showToastNotification('Product updated successfully!', 'success');
  };

  const deleteProduct = (productId) => {
    const product = products.find(p => p.id === productId);
    setProducts(prev => prev.filter(product => product.id !== productId));
    showToastNotification(`"${product.name}" deleted successfully!`, 'error');
  };

  const value = {
    products,
    addProduct,
    updateProduct,
    deleteProduct,
    showToast
  };

  return (
    <ProductContext.Provider value={value}>
      {children}
      {showToast.show && (
        <div
          className={`fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 transition-all duration-300 ${
            showToast.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          } text-white`}
        >
          {showToast.message}
        </div>
      )}
    </ProductContext.Provider>
  );
};
